import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Bucket, BucketTheme, Todo, BucketType, Idea, User, UserSettings, DesignType } from './types';
import { BucketCard } from './components/BucketCard';
import { BucketRow } from './components/BucketRow';
import { StatisticsView } from './components/StatisticsView';
import { CalendarView } from './components/CalendarView';
import { AllTasksView } from './components/AllTasksView';
import { ActiveTaskOverlay } from './components/ActiveTaskOverlay';
import { EditTaskModal } from './components/EditTaskModal';
import { IdeasView } from './components/IdeasView';
import { PreferencesView } from './components/PreferencesView';
import { AuthView } from './components/AuthView';
import { dbService } from './services/dbService';
import { Plus, LayoutGrid, List, Target, Calendar, BarChart2, Bell, Globe, Clock, Lightbulb, Flag, ChevronDown, Check, LogOut, User as UserIcon, Settings, X, Info, Cloud, CloudOff, RefreshCw, FlaskConical, Image as ImageIcon, Palette, Rocket } from 'lucide-react';
import { LanguageContext, translations, Language, DEFAULT_LEVEL_TITLES } from './i18n';

const THEME_OPTIONS: { value: BucketTheme; color: string }[] = [
  { value: BucketTheme.Blue, color: 'bg-blue-500' },
  { value: BucketTheme.Purple, color: 'bg-purple-500' },
  { value: BucketTheme.Green, color: 'bg-green-500' },
  { value: BucketTheme.Orange, color: 'bg-orange-500' },
  { value: BucketTheme.Pink, color: 'bg-pink-500' },
  { value: BucketTheme.Red, color: 'bg-red-500' },
  { value: BucketTheme.Teal, color: 'bg-teal-500' },
  { value: BucketTheme.Yellow, color: 'bg-yellow-500' },
  { value: BucketTheme.Indigo, color: 'bg-indigo-500' },
  { value: BucketTheme.Cyan, color: 'bg-cyan-500' },
  { value: BucketTheme.Lime, color: 'bg-lime-500' },
  { value: BucketTheme.Rose, color: 'bg-rose-500' },
];

interface AlertItem {
    type: 'recurring' | 'scheduled';
    bucket: Bucket;
    remaining?: number;
    target?: number;
    count?: number;
}

export interface ExternalTodo {
  todo: Todo;
  sourceBucketId: string;
  sourceBucketName: string;
  sourceBucketTheme: BucketTheme;
}

interface TaskModalState {
    isOpen: boolean;
    mode: 'create' | 'edit';
    bucketId: string;
    todo?: Todo;
}

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('lifebuckets-current-user');
    return saved ? JSON.parse(saved) : null;
  });

  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [settings, setSettings] = useState<UserSettings>({});
  const [language, setLanguage] = useState<Language>('en');
  
  // Sync Status
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error' | 'idle'>('idle');
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDevMode = localStorage.getItem('lifebuckets-dev-mode') === 'true';

  const t = translations[language];

  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'stats' | 'calendar' | 'allTasks' | 'ideas' | 'preferences'>('grid');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  
  const navRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  
  const [editingBucketId, setEditingBucketId] = useState<string | null>(null);
  const [taskModal, setTaskModal] = useState<TaskModalState>({ isOpen: false, mode: 'create', bucketId: '' });
  const [newBucketName, setNewBucketName] = useState('');
  const [newBucketTheme, setNewBucketTheme] = useState<BucketTheme>(BucketTheme.Blue);
  const [newBucketImage, setNewBucketImage] = useState('');
  const [newBucketType, setNewBucketType] = useState<BucketType>(BucketType.Standard);
  const [newBucketDescription, setNewBucketDescription] = useState('');
  const [hasGoal, setHasGoal] = useState(false);
  const [goalTitle, setGoalTitle] = useState('');
  const [targetPoints, setTargetPoints] = useState(50);
  const [deadlineDate, setDeadlineDate] = useState('');
  const [hasRecurring, setHasRecurring] = useState(false);
  const [recurringPoints, setRecurringPoints] = useState(20);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');

  const currentDesign = settings.design || 'original';

  const memoizedT = useMemo(() => {
    const base = translations[language];
    return {
      ...base,
      levelTitles: settings.levelTitles || base.levelTitles || DEFAULT_LEVEL_TITLES
    };
  }, [language, settings.levelTitles]);

  const seedDefaultBuckets = () => {
    const now = Date.now();
    const seededBuckets: Bucket[] = [
      {
        id: crypto.randomUUID(),
        name: language === 'en' ? 'Learning' : 'Lernen',
        theme: BucketTheme.Blue,
        type: BucketType.Standard,
        createdAt: now,
        todos: [
          { id: crypto.randomUUID(), text: language === 'en' ? 'Read 10 pages' : '10 Seiten lesen', points: 2, completed: false, createdAt: now, difficulty: 'easy', timeConsumption: 'short' },
          { id: crypto.randomUUID(), text: language === 'en' ? 'Watch coding course' : 'Programmierkurs schauen', points: 5, completed: false, createdAt: now, difficulty: 'medium', timeConsumption: 'long' }
        ]
      }
    ];
    return seededBuckets;
  };

  useEffect(() => {
    document.documentElement.className = `design-${currentDesign}`;
    document.documentElement.setAttribute('data-design', currentDesign);
    
    if (settings.designColors) {
      document.documentElement.style.setProperty('--lb-primary', settings.designColors.primary);
      document.documentElement.style.setProperty('--lb-secondary', settings.designColors.secondary);
    } else {
      document.documentElement.style.removeProperty('--lb-primary');
      document.documentElement.style.removeProperty('--lb-secondary');
    }
  }, [currentDesign, settings.designColors]);

  useEffect(() => {
    if (currentUser) {
      const loadData = async () => {
        setIsInitialLoading(true);
        const savedBuckets = localStorage.getItem(`lifebuckets-data-${currentUser.id}`);
        const savedIdeas = localStorage.getItem(`lifebuckets-ideas-${currentUser.id}`);
        const savedSettings = localStorage.getItem(`lifebuckets-settings-${currentUser.id}`);

        if (savedBuckets) setBuckets(JSON.parse(savedBuckets));
        if (savedIdeas) setIdeas(JSON.parse(savedIdeas));
        if (savedSettings) setSettings(JSON.parse(savedSettings));

        setSyncStatus('syncing');
        const remoteData = await dbService.fetchData(currentUser.id);
        if (remoteData) {
          if (remoteData.buckets) setBuckets(remoteData.buckets);
          if (remoteData.ideas) setIdeas(remoteData.ideas);
          if (remoteData.settings) setSettings(remoteData.settings);
          setSyncStatus('synced');
        } else {
          setSyncStatus(isDevMode ? 'idle' : 'error');
          if (!savedBuckets) setBuckets(seedDefaultBuckets());
        }
        setIsInitialLoading(false);
      };
      loadData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && !isInitialLoading) {
      localStorage.setItem(`lifebuckets-data-${currentUser.id}`, JSON.stringify(buckets));
      localStorage.setItem(`lifebuckets-ideas-${currentUser.id}`, JSON.stringify(ideas));
      localStorage.setItem(`lifebuckets-settings-${currentUser.id}`, JSON.stringify(settings));

      if (isDevMode) {
        setSyncStatus('idle');
        return;
      }

      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      setSyncStatus('syncing');
      syncTimeoutRef.current = setTimeout(async () => {
        const success = await dbService.pushData(currentUser.id!, { buckets, ideas, settings });
        setSyncStatus(success ? 'synced' : 'error');
      }, 3000);
    }
  }, [buckets, ideas, settings, currentUser]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setIsNavOpen(false);
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) setIsNotifOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogin = (user: User) => {
    setCurrentUser(user);
    localStorage.setItem('lifebuckets-current-user', JSON.stringify(user));
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('lifebuckets-current-user');
    setIsNavOpen(false);
    setIsNotifOpen(false);
    setBuckets([]);
    setIdeas([]);
    setSettings({});
  };

  const handleImportData = (data: { buckets?: Bucket[], ideas?: Idea[], settings?: UserSettings }) => {
    if (data.buckets) setBuckets(data.buckets);
    if (data.ideas) setIdeas(data.ideas);
    if (data.settings) setSettings(data.settings);
  };

  const activeTaskData = useMemo(() => {
      for (const bucket of buckets) {
          const activeTodo = bucket.todos.find(t => !!t.activeSessionStart);
          if (activeTodo) return { todo: activeTodo, bucket };
      }
      return null;
  }, [buckets]);

  const getExternalTodos = (targetBucketId: string): ExternalTodo[] => {
      const external: ExternalTodo[] = [];
      buckets.forEach(b => {
          if (b.id === targetBucketId) return;
          b.todos.forEach(t => {
              if (t.contributesTo?.includes(targetBucketId)) {
                  external.push({
                      todo: t,
                      sourceBucketId: b.id,
                      sourceBucketName: b.name,
                      sourceBucketTheme: b.theme
                  });
              }
          });
      });
      return external;
  };

  const alerts = useMemo(() => {
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const results: AlertItem[] = [];

    buckets.forEach(b => {
         if (b.type === BucketType.FokusSprint && b.endDate && Date.now() > b.endDate) return;
         const dueToday = b.todos.filter(t => {
             if (t.completed || !t.scheduledDate) return false;
             const d = new Date(t.scheduledDate);
             return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` === todayKey;
         });
         if (dueToday.length > 0) results.push({ type: 'scheduled', bucket: b, count: dueToday.length });
    });
    return results;
  }, [buckets]);

  const handleAddIdea = (text: string) => {
    const newIdea: Idea = { id: crypto.randomUUID(), text, createdAt: Date.now() };
    setIdeas([newIdea, ...ideas]);
  };

  const handleDeleteIdea = (id: string) => {
      if(confirm("Delete this idea?")) setIdeas(ideas.filter(i => i.id !== id));
  };

  const handleSaveSettings = (newSettings: UserSettings) => {
    setSettings(newSettings);
  };

  const openCreateModal = () => {
    setEditingBucketId(null);
    setNewBucketName('');
    setNewBucketTheme(BucketTheme.Blue);
    setNewBucketImage('');
    setNewBucketType(BucketType.Standard);
    setNewBucketDescription('');
    setHasGoal(false);
    setGoalTitle('');
    setTargetPoints(50);
    setDeadlineDate(new Date(Date.now() + 2592000000).toISOString().split('T')[0]);
    setHasRecurring(false);
    setRecurringPoints(20);
    setNewStartDate(new Date().toISOString().split('T')[0]);
    setNewEndDate(new Date(Date.now() + 2592000000).toISOString().split('T')[0]);
    setIsModalOpen(true);
  };

  const openEditModal = (bucket: Bucket) => {
    setEditingBucketId(bucket.id);
    setNewBucketName(bucket.name);
    setNewBucketTheme(bucket.theme);
    setNewBucketImage(bucket.headerImage || '');
    setNewBucketType(bucket.type || BucketType.Standard);
    setNewBucketDescription(bucket.description || '');
    if (bucket.type === BucketType.Standard) {
        if (bucket.goal) {
            setHasGoal(true); setGoalTitle(bucket.goal.title || ''); setTargetPoints(bucket.goal.targetPoints);
            setDeadlineDate(new Date(bucket.goal.deadline).toISOString().split('T')[0]);
        } else {
            setHasGoal(false); setGoalTitle(''); setTargetPoints(50);
            setDeadlineDate(new Date(Date.now() + 2592000000).toISOString().split('T')[0]);
        }
        setHasRecurring(!!bucket.recurringGoal);
        setRecurringPoints(bucket.recurringGoal?.targetPoints || 20);
    } else if (bucket.type === BucketType.FokusSprint) {
        setNewStartDate(bucket.startDate ? new Date(bucket.startDate).toISOString().split('T')[0] : '');
        setNewEndDate(bucket.endDate ? new Date(bucket.endDate).toISOString().split('T')[0] : '');
    }
    setIsModalOpen(true);
  };

  const handleSaveBucket = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBucketName.trim()) return;
    let goalData = undefined, recurringData = undefined, startDate = undefined, endDate = undefined;
    if (newBucketType === BucketType.Standard) {
        goalData = hasGoal ? { title: goalTitle, targetPoints: targetPoints, deadline: new Date(deadlineDate).getTime() } : undefined;
        recurringData = hasRecurring ? { targetPoints: recurringPoints, interval: 'month' as const } : undefined;
    } else {
        startDate = new Date(newStartDate).getTime(); endDate = new Date(newEndDate).getTime();
    }
    if (editingBucketId) {
        setBuckets(buckets.map(b => b.id === editingBucketId ? {
            ...b, 
            name: newBucketName, 
            theme: newBucketTheme, 
            type: newBucketType, 
            description: newBucketDescription.trim() || undefined,
            headerImage: newBucketImage.trim() || undefined,
            goal: newBucketType === BucketType.Standard ? goalData : undefined,
            recurringGoal: newBucketType === BucketType.Standard ? recurringData : undefined,
            startDate: newBucketType === BucketType.FokusSprint ? startDate : undefined,
            endDate: newBucketType === BucketType.FokusSprint ? endDate : undefined,
        } : b));
    } else {
        const newBucket: Bucket = {
            id: crypto.randomUUID(), 
            name: newBucketName, 
            theme: newBucketTheme, 
            type: newBucketType, 
            description: newBucketDescription.trim() || undefined,
            headerImage: newBucketImage.trim() || undefined,
            todos: [], 
            createdAt: Date.now(), 
            goal: goalData, 
            recurringGoal: recurringData, 
            milestones: [], 
            startDate, 
            endDate
        };
        setBuckets([...buckets, newBucket]);
    }
    setIsModalOpen(false);
  };

  const updateBucket = (id: string, updates: Partial<Bucket>) => {
    setBuckets(buckets.map(b => b.id === id ? { ...b, ...updates } : b));
  };

  const deleteBucket = (id: string) => {
    if (confirm(memoizedT.deleteBucketConfirm)) setBuckets(buckets.filter(b => b.id !== id));
  };

  const handleToggleActiveTask = (bucketId: string, todoId: string) => {
    setBuckets(prevBuckets => {
      const now = Date.now();
      return prevBuckets.map(b => ({
        ...b,
        todos: b.todos.map(t => {
          if (b.id === bucketId && t.id === todoId) {
            if (t.activeSessionStart) return { ...t, activeSessionStart: undefined, timeSpent: (t.timeSpent || 0) + (now - t.activeSessionStart) };
            else return { ...t, activeSessionStart: now };
          }
          if (t.activeSessionStart) return { ...t, activeSessionStart: undefined, timeSpent: (t.timeSpent || 0) + (now - t.activeSessionStart) };
          return t;
        })
      }));
    });
  };

  const handleManageTask = (bucketId: string, todo?: Todo) => {
      setTaskModal({ isOpen: true, mode: todo ? 'edit' : 'create', bucketId, todo });
  };

  const handleSaveTaskFromModal = (data: Partial<Todo>) => {
      if (!taskModal.bucketId) return;
      const bucket = buckets.find(b => b.id === taskModal.bucketId);
      if (!bucket) return;
      if (taskModal.mode === 'create') {
          const newTodo: Todo = { id: crypto.randomUUID(), text: data.text || '', points: data.points || 1, completed: false, createdAt: Date.now(), ...data };
          updateBucket(bucket.id, { todos: [...bucket.todos, newTodo] });
      } else if (taskModal.mode === 'edit' && taskModal.todo) {
          const updatedTodos = bucket.todos.map(t => t.id === taskModal.todo?.id ? { ...t, ...data } : t);
          updateBucket(bucket.id, { todos: updatedTodos });
      }
  };

  const navItems = [
    { id: 'grid', label: memoizedT.gridView, icon: LayoutGrid, desc: 'Bucket Overview' },
    { id: 'list', label: memoizedT.listView, icon: List, desc: 'Detailed List' },
    { id: 'allTasks', label: memoizedT.allTasksView, icon: Target, desc: 'Task Planner' },
    { id: 'calendar', label: memoizedT.calendarView, icon: Calendar, desc: 'Timeline' },
    { id: 'stats', label: memoizedT.statistics, icon: BarChart2, desc: 'Analytics' },
    { id: 'ideas', label: memoizedT.ideasView, icon: Lightbulb, desc: 'Brainstorm' },
    { id: 'preferences', label: memoizedT.preferences, icon: Settings, desc: memoizedT.preferencesDesc },
  ];

  if (isInitialLoading) {
      return (
          <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold animate-bounce shadow-2xl shadow-indigo-500/20 mb-8">LB</div>
              <p className="text-slate-400 font-medium tracking-widest uppercase text-xs animate-pulse">{isDevMode ? 'Loading Local Data...' : 'Syncing with D1 Cloud...'}</p>
          </div>
      );
  }

  if (!currentUser) {
    return (
      <LanguageContext.Provider value={{ language, setLanguage, t: memoizedT }}>
        <AuthView onLogin={handleLogin} />
      </LanguageContext.Provider>
    );
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: memoizedT }}>
    <div className={`min-h-screen pb-6`}>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f172a]/90 backdrop-blur-lg border-b border-white/5 h-[calc(4rem+var(--safe-area-inset-top))]">
         <div className="max-w-7xl mx-auto px-4 h-full flex items-end pb-4 justify-between" style={{ paddingTop: 'var(--safe-area-inset-top)' }}>
             
             <div className="relative" ref={navRef}>
                 <button onClick={() => setIsNavOpen(!isNavOpen)} className="flex items-center gap-3 hover:bg-white/5 px-2 py-1.5 rounded-xl transition-all active:scale-95 group">
                     <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-500/20 group-hover:shadow-indigo-500/40 transition-shadow shrink-0">LB</div>
                     <div className="flex flex-col items-start leading-tight">
                        <div className="flex items-center gap-1.5">
                            <h1 className="text-xl font-bold text-white">{memoizedT.appName}</h1>
                            <ChevronDown size={14} className={`text-slate-500 transition-transform duration-300 ${isNavOpen ? 'rotate-180' : ''}`} />
                        </div>
                        <span className="text-[10px] uppercase font-black tracking-[0.2em] text-indigo-400 opacity-80">{navItems.find(i => i.id === viewMode)?.label || 'Menu'}</span>
                     </div>
                 </button>

                 {isNavOpen && (
                    <div className="absolute top-14 left-0 w-72 bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl p-2 animate-in fade-in zoom-in-95 duration-200 z-[60]">
                        <div className="p-4 mb-2 border-b border-white/5">
                           <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-slate-800 border border-white/10 overflow-hidden"><img src={currentUser.avatar} alt="" className="w-full h-full object-cover" /></div>
                              <div className="min-w-0">
                                 <p className="text-sm font-bold text-white truncate">{currentUser.username}</p>
                                 <p className="text-[10px] text-slate-500 truncate">{currentUser.email}</p>
                              </div>
                           </div>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                            {navItems.map((item) => (
                                <button key={item.id} onClick={() => { setViewMode(item.id as any); setIsNavOpen(false); }} className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all group ${viewMode === item.id ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                                    <div className={`p-2 rounded-lg ${viewMode === item.id ? 'bg-white/20' : 'bg-slate-800 group-hover:bg-slate-700'}`}><item.icon size={18} /></div>
                                    <div className="flex-1 text-left"><p className="text-sm font-bold">{item.label}</p><p className={`text-[10px] uppercase tracking-wider font-medium opacity-60 ${viewMode === item.id ? 'text-indigo-100' : 'text-slate-500'}`}>{item.desc}</p></div>
                                    {viewMode === item.id && <Check size={16} className="text-white/80" />}
                                </button>
                            ))}
                        </div>
                        <div className="mt-2 pt-2 border-t border-white/5">
                           <button onClick={handleLogout} className="w-full flex items-center gap-4 p-3 rounded-xl transition-all text-red-400 hover:bg-red-500/10"><div className="p-2 rounded-lg bg-red-500/10"><LogOut size={18} /></div><span className="text-sm font-bold">{memoizedT.logout}</span></button>
                        </div>
                    </div>
                 )}
             </div>

             <div className="flex items-center gap-2 sm:gap-4">
                 <div className="relative" ref={notifRef}>
                    <button onClick={() => setIsNotifOpen(!isNotifOpen)} className={`p-2.5 rounded-xl transition-colors relative ${isNotifOpen ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                        <Bell size={20} />
                        {alerts.length > 0 && <span className="absolute top-2.5 right-2.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-[#0f172a] animate-pulse"></span>}
                    </button>

                    {isNotifOpen && (
                        <div className="fixed top-[calc(4.5rem+var(--safe-area-inset-top))] left-4 right-4 sm:absolute sm:top-14 sm:left-auto sm:right-0 sm:w-80 max-w-sm bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl p-2 animate-in fade-in slide-in-from-top-4 duration-200 z-[60]">
                            <div className="p-3 border-b border-white/5 flex items-center justify-between"><span className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">{memoizedT.reminders}</span>{alerts.length > 0 && <span className="px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400 text-[10px] font-bold">{alerts.length} {memoizedT.active}</span>}</div>
                            <div className="max-h-96 overflow-y-auto custom-scrollbar p-1">
                                {alerts.length === 0 ? (
                                    <div className="py-10 flex flex-col items-center justify-center text-center opacity-40"><div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 mb-3"><Check size={24} /></div><p className="text-sm font-bold text-white">{memoizedT.allCaughtUp}</p></div>
                                ) : (
                                    <div className="space-y-1">
                                        {alerts.map((alert, idx) => (
                                            <div key={idx} className="p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all flex gap-3">
                                                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-emerald-500/20 text-emerald-400"><Calendar size={18} /></div>
                                                <div className="flex-1 min-w-0"><p className="text-xs font-bold text-white mb-0.5 truncate">{alert.bucket.name}</p><p className="text-[11px] text-slate-400 leading-snug">{memoizedT.scheduledTaskReminder(alert.count || 0)}</p></div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                 </div>

                 <button onClick={openCreateModal} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-indigo-600/20 active:scale-95 shrink-0"><Plus size={18} /><span className="text-sm hidden sm:inline">{memoizedT.newBucket}</span></button>
                 <button onClick={() => setLanguage(language === 'en' ? 'de' : 'en')} className="p-2.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl"><Globe size={20} /></button>
             </div>
         </div>
      </nav>

      <main className="pt-24 px-4 max-w-7xl mx-auto min-h-[calc(100vh-6rem)]" style={{ paddingTop: 'calc(4rem + var(--safe-area-inset-top) + 1rem)', paddingBottom: 'calc(2rem + var(--safe-area-inset-bottom))' }}>
        <div className="animate-in fade-in duration-500">
            {viewMode === 'grid' && (
              buckets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center animate-in fade-in duration-700">
                    <div className="w-24 h-24 bg-indigo-500/10 rounded-full flex items-center justify-center mb-6"><Target size={48} className="text-indigo-400" /></div>
                    <h2 className="text-3xl font-bold text-white mb-2">{memoizedT.introTitlePrefix} <span className="text-indigo-400">{memoizedT.introTitleHighlight}</span>{memoizedT.introTitleSuffix}</h2>
                    <p className="text-slate-500 mt-2">{memoizedT.introSubtitle}</p>
                    <button onClick={openCreateModal} className="bg-white text-slate-900 px-6 py-3 rounded-xl font-bold mt-8 flex items-center gap-2 shadow-xl hover:scale-105 transition-transform"><Plus size={20} />{memoizedT.createFirstBucket}</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{buckets.map(bucket => <BucketCard key={bucket.id} bucket={bucket} externalTodos={getExternalTodos(bucket.id)} otherBuckets={buckets.filter(b => b.id !== bucket.id)} onUpdateBucket={updateBucket} onDeleteBucket={deleteBucket} onEditBucket={openEditModal} onToggleActiveTask={handleToggleActiveTask} onManageTask={handleManageTask} currentDesign={currentDesign} />)}</div>
              )
            )}
            {viewMode === 'list' && (<div className="space-y-4 max-w-4xl mx-auto">{buckets.map(bucket => <BucketRow key={bucket.id} bucket={bucket} externalTodos={getExternalTodos(bucket.id)} onEdit={openEditModal} onDelete={deleteBucket} />)}</div>)}
            {viewMode === 'allTasks' && <AllTasksView buckets={buckets} onUpdateBucket={updateBucket} onToggleActiveTask={handleToggleActiveTask} onJumpAndEdit={() => {}} onManageTask={handleManageTask} onCreateBucket={openCreateModal} />}
            {viewMode === 'calendar' && <CalendarView buckets={buckets} onUpdateBucket={updateBucket} onDeleteBucket={deleteBucket} onCreateBucket={openCreateModal} />}
            {viewMode === 'stats' && <StatisticsView buckets={buckets} onCreateBucket={openCreateModal} />}
            {viewMode === 'ideas' && <IdeasView ideas={ideas} onAddIdea={handleAddIdea} onDeleteIdea={handleDeleteIdea} />}
            {viewMode === 'preferences' && <PreferencesView settings={settings} onSave={handleSaveSettings} buckets={buckets} ideas={ideas} onImport={handleImportData} />}
        </div>
      </main>

      {activeTaskData && <ActiveTaskOverlay todo={activeTaskData.todo} bucketName={activeTaskData.bucket.name} bucketTheme={activeTaskData.bucket.theme} onStop={() => handleToggleActiveTask(activeTaskData.bucket.id, activeTaskData.todo.id)} onComplete={() => {}} />}
      <EditTaskModal isOpen={taskModal.isOpen} mode={taskModal.mode} bucketId={taskModal.bucketId} buckets={buckets} initialData={taskModal.todo} onClose={() => setTaskModal(prev => ({ ...prev, isOpen: false }))} onSave={handleSaveTaskFromModal} />
      
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#1e293b] rounded-3xl p-6 w-full max-w-lg border border-white/10 shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
            <h2 className="text-2xl font-bold mb-6 text-white">{editingBucketId ? memoizedT.editBucketTitle : memoizedT.createBucketTitle}</h2>
            <form onSubmit={handleSaveBucket} className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{memoizedT.bucketType}</label>
                <div className="grid grid-cols-2 gap-3">
                  <div onClick={() => setNewBucketType(BucketType.Standard)} className={`cursor-pointer p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${newBucketType === BucketType.Standard ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}>
                    <Target size={24} className={newBucketType === BucketType.Standard ? 'text-indigo-400' : 'text-slate-400'} />
                    <p className={`font-bold text-[11px] uppercase tracking-wider ${newBucketType === BucketType.Standard ? 'text-white' : 'text-slate-300'}`}>{memoizedT.typeStandard}</p>
                  </div>
                  <div onClick={() => setNewBucketType(BucketType.FokusSprint)} className={`cursor-pointer p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${newBucketType === BucketType.FokusSprint ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'}`}>
                    <Rocket size={24} className={newBucketType === BucketType.FokusSprint ? 'text-indigo-400' : 'text-slate-400'} />
                    <p className={`font-bold text-[11px] uppercase tracking-wider ${newBucketType === BucketType.FokusSprint ? 'text-white' : 'text-slate-300'}`}>{memoizedT.typeFokusSprint}</p>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 italic mt-1">
                  {newBucketType === BucketType.FokusSprint ? memoizedT.fokusSprintDesc : ''}
                </p>
              </div>

              <div>
                <label className="block text-slate-400 text-sm font-bold mb-2 uppercase tracking-wider">{memoizedT.bucketName}</label>
                <input type="text" value={newBucketName} onChange={(e) => setNewBucketName(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500" placeholder={memoizedT.placeholderBucketName} />
              </div>

              <div>
                <label className="block text-slate-400 text-sm font-bold mb-2 uppercase tracking-wider">{memoizedT.description}</label>
                <textarea 
                  value={newBucketDescription} 
                  onChange={(e) => setNewBucketDescription(e.target.value)} 
                  rows={2}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 resize-none" 
                  placeholder={memoizedT.placeholderDescription} 
                />
              </div>
              
              <div>
                <label className="block text-slate-400 text-sm font-bold mb-2 uppercase tracking-wider">{memoizedT.colorTheme}</label>
                <div className="grid grid-cols-6 gap-2">{THEME_OPTIONS.map((theme) => (<button key={theme.value} type="button" onClick={() => setNewBucketTheme(theme.value)} className={`w-full aspect-square rounded-lg transition-all ${theme.color} ${newBucketTheme === theme.value ? 'ring-2 ring-white ring-offset-2 ring-offset-[#1e293b] scale-110' : 'opacity-60 hover:opacity-100'}`} />))}</div>
              </div>
              
              {newBucketType === BucketType.Standard ? (
                  <div className="space-y-4 pt-2 border-t border-white/5"><div className="flex items-center justify-between"><div className="flex items-center gap-2"><Flag size={18} className={hasGoal ? 'text-indigo-400' : 'text-slate-500'} /><span className="font-bold text-slate-300">{memoizedT.totalGoal}</span></div><button type="button" onClick={() => setHasGoal(!hasGoal)} className={`w-12 h-6 rounded-full transition-colors relative ${hasGoal ? 'bg-indigo-600' : 'bg-slate-700'}`}><div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${hasGoal ? 'left-7' : 'left-1'}`}></div></button></div>{hasGoal && (<div className="bg-slate-900/50 p-4 rounded-xl space-y-4 border border-white/5"><input type="text" value={goalTitle} onChange={(e) => setGoalTitle(e.target.value)} placeholder={memoizedT.placeholderMilestone} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" /><div className="grid grid-cols-2 gap-4"><input type="number" value={targetPoints} onChange={(e) => setTargetPoints(parseInt(e.target.value) || 0)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" /><input type="date" value={deadlineDate} onChange={(e) => setDeadlineDate(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white [color-scheme:dark]" /></div></div>)}</div>
              ) : (
                   <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/5">
                      <div>
                        <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">{memoizedT.startDate}</label>
                        <input type="date" value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white [color-scheme:dark]" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">{memoizedT.endDate}</label>
                        <input type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white [color-scheme:dark]" />
                      </div>
                   </div>
              )}
              <div className="flex gap-3 pt-4"><button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3 bg-slate-800 text-slate-300 rounded-xl font-bold">{memoizedT.cancel}</button><button type="submit" className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold">{editingBucketId ? memoizedT.saveChanges : memoizedT.createBucketAction}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
    </LanguageContext.Provider>
  );
}
export default App;